"use client";

import Link from "next/link";
import type { ReactNode } from "react";
import { useMemo } from "react";

type ToolFrameProps = {
  toolKey: string;
  title: string;
  embedPath: string;
  children: ReactNode;
  exportCsvRows?: Array<Record<string, string | number>>;
};

function toCsv(rows: Array<Record<string, string | number>>) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const body = rows.map((r) =>
    headers
      .map((h) => {
        const v = String(r[h] ?? "");
        return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      })
      .join(","),
  );
  return `# ExpiryDesk tool export\n${headers.join(",")}\n${body.join("\n")}\n`;
}

export function ToolFrame({ toolKey, title, embedPath, children, exportCsvRows = [] }: ToolFrameProps) {
  const embedCode = useMemo(
    () => `<iframe src="https://expirydesk.co.za${embedPath}" width="100%" height="700" style="border:0;"></iframe>`,
    [embedPath],
  );

  function exportCsv() {
    const csv = toCsv(exportCsvRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${toolKey}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div>
      {children}
      <div className="ed-card-glass" style={{ marginTop: "1.25rem", padding: "1rem", borderRadius: "12px" }}>
        <p style={{ margin: "0 0 0.45rem", fontSize: ".76rem", color: "#64748b", letterSpacing: ".05em", textTransform: "uppercase", fontWeight: 700 }}>
          Embed
        </p>
        <code style={{ display: "block", whiteSpace: "pre-wrap", fontSize: ".75rem", color: "#334155", background: "rgba(248,250,252,.8)", padding: ".55rem .65rem", borderRadius: "8px" }}>
          {embedCode}
        </code>
        <div style={{ marginTop: ".7rem", display: "flex", gap: ".55rem", flexWrap: "wrap" }}>
          <button type="button" className="ed-btn-secondary" onClick={() => void navigator.clipboard?.writeText(embedCode)}>
            Copy embed code
          </button>
          <button type="button" className="ed-btn-secondary" onClick={exportCsv} disabled={!exportCsvRows.length}>
            Export CSV
          </button>
        </div>
      </div>

      <aside style={{ marginTop: "1rem", padding: "1rem 1.1rem", borderRadius: "14px", border: "1px solid rgba(14,165,233,.25)", background: "rgba(240,249,255,.6)" }}>
        <p style={{ margin: "0 0 .5rem", fontSize: ".78rem", letterSpacing: ".04em", color: "#64748b", fontWeight: 700 }}>UNLOCK FULL WORKFLOW</p>
        <p style={{ margin: "0 0 .8rem", lineHeight: 1.6, color: "#334155" }}>
          {title} solves one task. ExpiryDesk automates your whole inventory lifecycle: FEFO monitoring, risk notifications, exports, and distributor-ready reports.
        </p>
        <Link href="/signup?utm_source=tool&utm_medium=cta&utm_campaign=trojan-tools" className="ed-landing-btn ed-landing-btn--primary">
          Unlock full workflow in ExpiryDesk
        </Link>
      </aside>
    </div>
  );
}

